import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import moment from "moment-timezone";

import breakpoints from "_constants/breakpoints";
import divideOnPages from "_helpers/divideOnPages";

import Pagination from "components/Pagination/Pagination";

const ORDERS_ON_PAGE = 12;

const Container = styled.div`
  padding: 48px 32px 56px;
  margin-bottom: 33px;
  background: #ffffff;
  box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);
  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      padding: 33px 12px 14px;

      @media (max-width: ${breakpoints.mobileM}) {
        padding: 16px;
      }
    `}
`;
const Headers = styled.div`
  display: flex;
  border-bottom: 1px solid #ecedf1;
`;
const Body = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #ecedf1;
  margin-bottom: 18px;
`;
const EmptyNotes = styled.div`
  padding: 16px 0;
`;
const OrderContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 18px 0px;
  border-bottom: 1px solid #ecedf1;
  &:nth-child(even) {
    background-color: #fafaff;
  }
  &:first-child {
    border-top: 1px solid #ecedf1;
  }
`;

const Cell = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  color: #7b848d;
  word-break: break-all;
`;
const DateCell = styled(Cell)`
  text-transform: uppercase;
`;
const Group = styled.div`
  display: flex;
  flex-direction: column;
  &:first-child {
    min-width: 90px;
  }
`;
const Label = styled(Cell)`
  font-weight: bold;
  flex-grow: 0;

  &:first-child {
    min-width: 90px;
  }
`;
const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      flex-direction: column;
      align-items: center;
    `};
`;
const Button = styled.button`
  width: 209px;
  height: 52px;
  background: #f37217;
  border: none;
  border-radius: 4px;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  color: #fafaff;
  ${({ theme: { mobile } }) =>
    mobile
      ? css`
          &:not(:last-child) {
            margin-bottom: 10px;
          }
        `
      : css`
          &:last-child {
            margin-left: auto;
          }
        `}
`;

function OrderStatus({ orderId, notes = [], specimen, search, monitoring }) {
  const [currentPage, setCurrentPage] = useState(1);

  const contentRef = useRef(null);

  const navigate = useNavigate();

  const handleChangePage = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
      contentRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const pages = divideOnPages(notes, ORDERS_ON_PAGE);

  const result = pages[currentPage - 1];

  return (
    <Container ref={contentRef}>
      <Headers>
        <Label>DATE & TIME</Label>
        <Label>DESCRIPTION</Label>
      </Headers>
      <Body>
        {notes.length ? (
          result?.map((note, i) => {
            return (
              <OrderContainer key={`${i}${note.date}`}>
                <Group>
                  <DateCell>{moment(note.date).format("MMM DD, YYYY")}</DateCell>
                  <DateCell>{moment(note.date).format("hh:mm A")}</DateCell>
                </Group>
                <Cell>{note.message}</Cell>
              </OrderContainer>
            );
          })
        ) : (
          <EmptyNotes>No Events for this order</EmptyNotes>
        )}
      </Body>

      <Pagination bottom currentPage={currentPage} totalPages={pages.length} onClick={handleChangePage} />
      <Footer>
        {specimen && (
          <Button onClick={() => navigate(`/orders/${orderId}/specimen`)}>
            Upload
            <br />
            Specimen/Website
          </Button>
        )}
        {search && <Button onClick={() => navigate(`/orders/${orderId}/search`)}>Search Results</Button>}
        {monitoring && <Button onClick={() => navigate(`/orders/${orderId}/monitoring`)}>Monitoring Reports</Button>}
      </Footer>
    </Container>
  );
}

OrderStatus.propTypes = {
  orderId: PropTypes.string.isRequired,
  notes: PropTypes.arrayOf(PropTypes.object),
  specimen: PropTypes.bool,
  search: PropTypes.bool,
  monitoring: PropTypes.bool,
};

export default OrderStatus;
